import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);

export interface PlatformUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  roles: string[];
  isActive?: boolean;
  tenantId?: string | null;
  tenant?: { name?: string } | null;
  lastLoginAt?: string | null;
  lockedUntil?: string | null; // set while a login lockout is active
  [k: string]: unknown;
}

export interface PlatformUsersPage {
  users: PlatformUser[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

// Platform scope: lists users across ALL tenants (ScopeGuard); pass tenantId to filter.
export const listAllUsers = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  tenantId?: string;
}) => axios.get<PlatformUsersPage>(`${API_V1}/users`, { params }).then((r) => r.data);

export const setUserActive = (id: string, active: boolean) =>
  axios.put(`${API_V1}/users/${id}/${active ? 'activate' : 'deactivate'}`).then((r) => r.data);

/** Clear a login lockout (repeated failed sign-ins) so the user can retry now,
 *  instead of waiting out the cool-off. Requires user:deactivate. */
export const unlockUser = (id: string) =>
  axios.put(`${API_V1}/users/${id}/unlock`).then((r) => r.data);

export interface ImpersonateResult {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string; tenantId: string };
}
/** Mint a session as the target user (audited). Requires user:impersonate. */
export const impersonateUser = (id: string) =>
  axios.post<ImpersonateResult>(`${API_V1}/users/${id}/impersonate`).then((r) => r.data);

/** Reset (disable) a user's 2FA so a locked-out user can sign in / re-enroll.
 *  Requires user:mfa:reset (platform scope covers all tenants). */
export const resetUserMfa = (id: string) =>
  axios.post<{ success: boolean; email: string }>(`${API_V1}/auth/mfa/admin/reset/${id}`).then((r) => r.data);
